import React, { useState, useRef, useEffect, useCallback } from "react";
import "./MediaCarousel.css";

/**
 * Universal & Reusable MediaCarousel Component
 * Supports Images, HTML5 Videos (.mp4, .webm), Embedded Videos (YouTube/Vimeo/LinkedIn/iFrames), and Custom Cards.
 * Features:
 * - Interactive Mouse Drag & Touch Swipe gestures
 * - Floating side navigation buttons on left & right
 * - Interactive dot pagination & slide counter
 *
 * @param {Array<string|Object>} items - Array of image/video URLs or objects { type: 'image'|'video'|'embed', src: string, poster?: string, alt?: string, title?: string }
 * @param {boolean} autoPlay - Whether slides should auto-advance
 * @param {number} autoPlayInterval - Time in milliseconds per slide (default 5000ms)
 * @param {boolean} showDots - Whether to show pagination dot indicators
 * @param {boolean} showArrows - Whether to show Next/Prev navigation arrows
 * @param {boolean} showCounter - Whether to show "1 / 8" slide counter
 * @param {string} className - Optional container class name
 * @param {number|string} height - Height for the carousel viewport (default: 500px)
 */
const MediaCarousel = ({
  items = [],
  autoPlay = false,
  autoPlayInterval = 5000,
  showDots = true,
  showArrows = true,
  showCounter = true,
  className = "",
  height = "520px",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isInView, setIsInView] = useState(false);

  const containerRef = useRef(null);
  const videoRefs = useRef({});
  const dragStartX = useRef(0);
  const dragCurrentX = useRef(0);
  const isDraggingRef = useRef(false);

  // IntersectionObserver to detect when the carousel is scrolled into view
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Helper to normalize video embed URLs (LinkedIn, YouTube, Vimeo, etc.)
  const normalizeEmbedUrl = (url) => {
    if (!url || typeof url !== "string") return url;

    // Convert LinkedIn post/activity/ugcPost URLs to embed URLs
    if (url.includes("linkedin.com") || url.includes("lnkd.in")) {
      const ugcMatch = url.match(/ugcPost[:-]?(\d+)/i);
      if (ugcMatch) {
        return `https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:${ugcMatch[1]}`;
      }
      const activityMatch = url.match(/activity[:-]?(\d+)/i);
      if (activityMatch) {
        return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activityMatch[1]}`;
      }
      if (url.includes("/embed/")) {
        return url;
      }
    }

    // Convert YouTube URLs to embed URLs
    if (url.includes("youtube.com/watch")) {
      try {
        const videoId = new URL(url).searchParams.get("v");
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      } catch (e) {}
    } else if (url.includes("youtu.be/")) {
      const videoId = url.split("youtu.be/")[1]?.split("?")[0];
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }

    return url;
  };

  // Normalize items to standard item objects
  const normalizedItems = items.map((item, index) => {
    if (typeof item === "string") {
      const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(item);
      const isEmbed = /(youtube\.com|youtu\.be|vimeo\.com|linkedin\.com|lnkd\.in)/i.test(item);
      return {
        type: isVideo ? "video" : isEmbed ? "embed" : "image",
        src: isEmbed ? normalizeEmbedUrl(item) : item,
        alt: `Media slide ${index + 1}`,
        title: `Slide ${index + 1}`,
      };
    }

    const isVideo = item.type === "video" || /\.(mp4|webm|ogg|mov)$/i.test(item.src);
    const isEmbed =
      item.type === "embed" ||
      /(youtube\.com|youtu\.be|vimeo\.com|linkedin\.com|lnkd\.in)/i.test(item.src);

    return {
      type: isVideo ? "video" : isEmbed ? "embed" : (item.type || "image"),
      src: isEmbed ? normalizeEmbedUrl(item.src) : item.src,
      poster: item.poster,
      alt: item.alt || item.title || `Media slide ${index + 1}`,
      title: item.title,
      caption: item.caption,
    };
  });

  const totalSlides = normalizedItems.length;

  // Pause videos when switching slides
  const pauseAllVideos = useCallback(() => {
    Object.values(videoRefs.current).forEach((videoEl) => {
      if (videoEl && typeof videoEl.pause === "function" && !videoEl.paused) {
        videoEl.pause();
      }
    });
  }, []);

  const goToSlide = useCallback(
    (index) => {
      pauseAllVideos();
      if (index >= totalSlides) {
        setCurrentIndex(0);
      } else if (index < 0) {
        setCurrentIndex(totalSlides - 1);
      } else {
        setCurrentIndex(index);
      }
    },
    [totalSlides, pauseAllVideos]
  );

  const nextSlide = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  // Video Autoplay & Playback Control based on active slide and scroll visibility
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idxStr, videoEl]) => {
      const idx = parseInt(idxStr, 10);
      if (!videoEl) return;

      if (idx === currentIndex) {
        if (isInView) {
          videoEl.muted = isMuted;
          const playPromise = videoEl.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // Handled browser autoplay policy gracefully
            });
          }
        } else {
          if (!videoEl.paused) {
            videoEl.pause();
          }
        }
      } else {
        if (!videoEl.paused) {
          videoEl.pause();
        }
        videoEl.currentTime = 0;
      }
    });
  }, [currentIndex, isMuted, isInView]);

  // Autoplay handler for non-video slides (only when in view)
  useEffect(() => {
    const currentItem = normalizedItems[currentIndex];
    const isCurrentVideo = currentItem?.type === "video";

    // For videos, slide transition is triggered automatically by onEnded
    if (isCurrentVideo) return;

    if (!autoPlay || isPaused || isDragging || !isInView || totalSlides <= 1) return;

    const timer = setTimeout(() => {
      nextSlide();
    }, autoPlayInterval);

    return () => clearTimeout(timer);
  }, [autoPlay, autoPlayInterval, isPaused, isDragging, isInView, nextSlide, totalSlides, currentIndex, normalizedItems]);

  // Drag / Swipe Handlers (Mouse & Touch)
  const handleDragStart = (clientX) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartX.current = clientX;
    dragCurrentX.current = clientX;
    setDragOffset(0);
  };

  const handleDragMove = (clientX) => {
    if (!isDraggingRef.current) return;
    dragCurrentX.current = clientX;
    const diff = clientX - dragStartX.current;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    const diff = dragCurrentX.current - dragStartX.current;
    const threshold = 50;

    if (diff < -threshold) {
      nextSlide();
    } else if (diff > threshold) {
      prevSlide();
    }
    setDragOffset(0);
  };

  // Mouse event listeners
  const onMouseDown = (e) => {
    // Only drag with left click and ignore video/button clicks
    if (e.button !== 0 || e.target.closest("button") || e.target.closest("video")) return;
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    handleDragMove(e.clientX);
  };

  const onMouseUp = () => {
    handleDragEnd();
  };

  const onMouseLeave = () => {
    if (isDraggingRef.current) {
      handleDragEnd();
    }
    setIsPaused(false);
  };

  // Touch event listeners
  const onTouchStart = (e) => {
    if (e.target.closest("button") || e.target.closest("video")) return;
    handleDragStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    handleDragMove(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    handleDragEnd();
  };

  if (totalSlides === 0) return null;

  return (
    <div
      ref={containerRef}
      className={`media-carousel-wrapper ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={onMouseLeave}
      aria-roledescription="carousel"
    >
      {/* Slide Viewport with Drag Support */}
      <div
        className={`media-carousel-viewport ${isDragging ? "dragging" : ""}`}
        style={{ height: typeof height === "number" ? `${height}px` : height }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Track */}
        <div
          className="media-carousel-track"
          style={{
            transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`,
            transition: isDragging ? "none" : "transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)",
          }}
        >
          {normalizedItems.map((item, index) => (
            <div
              className={`media-carousel-slide ${index === currentIndex ? "active" : ""}`}
              key={index}
              aria-hidden={index !== currentIndex}
            >
              {/* IMAGE TYPE */}
              {item.type === "image" && (
                <div className="media-carousel-content-box">
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="media-carousel-img"
                    draggable="false"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                  {item.caption && (
                    <div className="media-carousel-caption">{item.caption}</div>
                  )}
                </div>
              )}

              {/* HTML5 VIDEO TYPE */}
              {item.type === "video" && (
                <div className="media-carousel-content-box video-box">
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={item.src}
                    poster={item.poster}
                    controls
                    playsInline
                    preload="auto"
                    muted={isMuted}
                    className="media-carousel-video"
                    onVolumeChange={(e) => {
                      setIsMuted(e.currentTarget.muted);
                    }}
                    onEnded={() => {
                      nextSlide();
                    }}
                  >
                    Your browser does not support HTML5 video.
                  </video>
                  {item.caption && (
                    <div className="media-carousel-video-badge">
                      <i className="fa-solid fa-play-circle mr-2"></i>
                      {item.caption}
                    </div>
                  )}
                  {/* Floating Sound / Unmute Quick Toggle */}
                  <button
                    type="button"
                    className="media-carousel-audio-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newMuted = !isMuted;
                      setIsMuted(newMuted);
                      if (videoRefs.current[index]) {
                        videoRefs.current[index].muted = newMuted;
                      }
                    }}
                    aria-label={isMuted ? "Unmute audio" : "Mute audio"}
                    title={isMuted ? "Click to unmute" : "Click to mute"}
                  >
                    <i className={`fa-solid ${isMuted ? "fa-volume-xmark" : "fa-volume-high"} mr-2`}></i>
                    <span>{isMuted ? "Unmute" : "Sound On"}</span>
                  </button>
                </div>
              )}

              {/* EMBED / IFRAME TYPE (YouTube/Vimeo/LinkedIn) */}
              {item.type === "embed" && (
                <div className="media-carousel-content-box embed-box">
                  <iframe
                    src={item.src}
                    title={item.title || `Embedded Video ${index + 1}`}
                    className="media-carousel-iframe"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Counter Badge */}
        {showCounter && totalSlides > 1 && (
          <div className="media-carousel-counter">
            <span>{currentIndex + 1}</span> / <span>{totalSlides}</span>
          </div>
        )}

        {/* Side Navigation Buttons (Floating on Left & Right) */}
        {showArrows && totalSlides > 1 && (
          <>
            <button
              type="button"
              className="media-carousel-side-btn prev-side-btn"
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
              }}
              aria-label="Previous Slide"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            <button
              type="button"
              className="media-carousel-side-btn next-side-btn"
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
              }}
              aria-label="Next Slide"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </>
        )}
      </div>

      {/* Bottom Dot Pagination Indicators */}
      {showDots && totalSlides > 1 && (
        <div className="media-carousel-dots" role="tablist">
          {normalizedItems.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`media-carousel-dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={index === currentIndex}
              role="tab"
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaCarousel;
